import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatePickerInput } from '@mantine/dates';
import {
  IconActivity,
  IconBarbell,
  IconChartBar,
  IconCheck,
  IconClock,
  IconHeartbeat,
  IconPlayerPlay,
  IconScale,
  IconTrendingUp,
  IconTrophy,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';

import { TrophyCaseGrid } from '@components/Athlete/TrophyCase';
import appStore from '@duck/app-store';
import { useRealtime, useRealtimeDomain } from '@lib/realtime';
import {
  fetchAthleteAchievements,
  fetchAthleteProgressCompliance,
  fetchAthleteProgressOverview,
  fetchAthleteProgressStrength,
  fetchAthleteProgressVolume,
  fetchWorkoutSessionLiveSummary,
} from '@util/athleteSession';
import { toErrorMessage } from '@util/toErrorMessage';

import classes from './Progress.module.css';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'strength', label: 'Strength' },
  { id: 'volume', label: 'Volume' },
  { id: 'compliance', label: 'Compliance' },
];

const INTENSITY_LABELS = {
  under_60: '<60%',
  '60_70': '60–70%',
  '70_80': '70–80%',
  '80_90': '80–90%',
  '90_plus': '90%+',
};

const REP_LABELS = {
  '1_3': '1–3',
  '4_6': '4–6',
  '7_10': '7–10',
  '11_15': '11–15',
  '15_plus': '15+',
};

const ZONE_LABELS = {
  under_120: '<120',
  '120_140': '120–140',
  '140_160': '140–160',
  '160_plus': '160+',
};

function ymd(value) {
  if (!value) return null;
  return dayjs(value).format('YYYY-MM-DD');
}

function formatVolume(lb) {
  if (lb == null || Number.isNaN(Number(lb))) return '—';
  return `${Math.round(Number(lb)).toLocaleString('en-US')} lb`;
}

function formatPct(value, digits = 0) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toFixed(digits)}%`;
}

function formatSignedPct(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const number = Number(value);
  const sign = number > 0 ? '+' : number < 0 ? '−' : '';
  return `${sign}${Math.abs(number).toFixed(1)}%`;
}

function formatDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatClock(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${minutes}:${String(secs).padStart(2, '0')}`;
}

function formatShortDate(value) {
  if (!value) return '—';
  const date = dayjs(value);
  if (!date.isValid()) return '—';
  return date.format('MMM D');
}

function maxBucketValue(buckets, key = 'count') {
  if (!Array.isArray(buckets) || buckets.length === 0) return 1;
  return Math.max(1, ...buckets.map((row) => Number(row[key]) || 0));
}

function BarList({ buckets, labelKey = 'name', valueKey = 'count', pctKey = 'pct' }) {
  const max = maxBucketValue(buckets, valueKey);
  if (!buckets?.length) {
    return <p className={classes.empty}>No data in this range.</p>;
  }
  return (
    <ul className={classes.barList}>
      {buckets.map((row) => {
        const value = Number(row[valueKey]) || 0;
        const width = Math.round((value / max) * 100);
        return (
          <li key={row.key || row[labelKey]} className={classes.barRow}>
            <span className={classes.barLabel}>{row[labelKey]}</span>
            <div className={classes.barTrack}>
              <div className={classes.barFill} style={{ width: `${width}%` }} />
            </div>
            <span className={classes.barValue}>
              {value}
              {row[pctKey] != null ? ` · ${formatPct(row[pctKey], 1)}` : ''}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function WeekLoadChart({ weeks }) {
  if (!weeks?.length) {
    return <p className={classes.empty}>No weekly load yet.</p>;
  }
  const maxVol = Math.max(1, ...weeks.map((w) => Number(w.volume_lb) || 0));
  const maxHard = Math.max(1, ...weeks.map((w) => Number(w.hard_sets) || 0));
  return (
    <div className={classes.weekChart}>
      {weeks.map((week) => {
        const volH = Math.round(((Number(week.volume_lb) || 0) / maxVol) * 100);
        const hardH = Math.round(((Number(week.hard_sets) || 0) / maxHard) * 100);
        return (
          <div key={String(week.week_start)} className={classes.weekCol}>
            <div className={classes.weekBars}>
              <div
                className={classes.weekVol}
                style={{ height: `${volH}%` }}
                title={formatVolume(week.volume_lb)}
              />
              <div
                className={classes.weekHard}
                style={{ height: `${hardH}%` }}
                title={`${week.hard_sets} hard sets`}
              />
            </div>
            <span className={classes.weekLabel}>{formatShortDate(week.week_start)}</span>
          </div>
        );
      })}
    </div>
  );
}

function Sparkline({ points, valueKey = 'estimated_max' }) {
  if (!points?.length) return <p className={classes.empty}>No history.</p>;
  const values = points.map((p) => Number(p[valueKey]) || 0);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(1, max - min);
  return (
    <div className={classes.sparkline}>
      {values.map((value, index) => {
        const height = Math.round(((value - min) / span) * 100);
        return (
          <div
            key={`${index}-${value}`}
            className={classes.sparkBar}
            style={{ height: `${Math.max(8, height)}%` }}
            title={String(value)}
          />
        );
      })}
    </div>
  );
}

export default function AthleteProgress() {
  const navigate = useNavigate();
  const athlete = appStore((state) => state.athlete);
  const { subscribe } = useRealtime();
  const athleteId = athlete?.athlete_id || athlete?.id;

  const [tab, setTab] = useState('overview');
  // null until server default range is applied (current/recent block, else last 30d → yesterday)
  const [range, setRange] = useState(null);
  const [rangeReady, setRangeReady] = useState(false);
  const skipRangeReloadRef = useRef(true);
  const [overview, setOverview] = useState(null);
  const [strength, setStrength] = useState(null);
  const [volume, setVolume] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [liveNow, setLiveNow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);

  const rangeParams = useMemo(() => {
    if (!range?.[0] || !range?.[1]) return {};
    return {
      startDate: ymd(range[0]),
      endDate: ymd(range[1]),
    };
  }, [range]);

  const applyProgressPayload = useCallback((progress) => {
    setOverview(progress);
    setLiveNow(progress?.live_now || null);
    if (progress?.start_date && progress?.end_date) {
      setRange([
        dayjs(progress.start_date).toDate(),
        dayjs(progress.end_date).toDate(),
      ]);
      setRangeReady(true);
    }
  }, []);

  const loadOverview = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const [progress, catalog] = await Promise.all([
          fetchAthleteProgressOverview(params),
          athleteId ? fetchAthleteAchievements(athleteId) : Promise.resolve([]),
        ]);
        applyProgressPayload(progress);
        setAchievements(catalog || []);
      } catch (error) {
        toast.error(toErrorMessage(error));
      } finally {
        setLoading(false);
      }
    },
    [athleteId, applyProgressPayload],
  );

  // First paint: omit dates so the server picks block / 30d defaults.
  useEffect(() => {
    skipRangeReloadRef.current = true;
    setRangeReady(false);
    setRange(null);
    loadOverview({});
  }, [athleteId, loadOverview]);

  // Later picker changes reload overview (skip the seed write from boot).
  useEffect(() => {
    if (!rangeReady) return undefined;
    if (skipRangeReloadRef.current) {
      skipRangeReloadRef.current = false;
      return undefined;
    }
    loadOverview(rangeParams);
    return undefined;
  }, [rangeReady, rangeParams, loadOverview]);

  useEffect(() => {
    if (!rangeReady) return undefined;
    if (tab === 'overview') return undefined;
    let cancelled = false;
    setTabLoading(true);
    const loader =
      tab === 'strength'
        ? fetchAthleteProgressStrength
        : tab === 'volume'
          ? fetchAthleteProgressVolume
          : fetchAthleteProgressCompliance;
    loader(rangeParams)
      .then((payload) => {
        if (cancelled) return;
        if (tab === 'strength') setStrength(payload);
        if (tab === 'volume') setVolume(payload);
        if (tab === 'compliance') setCompliance(payload);
      })
      .catch((error) => {
        if (!cancelled) toast.error(toErrorMessage(error));
      })
      .finally(() => {
        if (!cancelled) setTabLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, rangeParams, rangeReady]);

  useEffect(() => subscribe({ channel: 'AlertChannel' }), [subscribe]);

  useRealtimeDomain('alert', (event) => {
    if (event?.type !== 'workout_session_live') return;
    if (event.payload) setLiveNow(event.payload);
  });

  useEffect(() => {
    if (!liveNow?.workout_session_id) return undefined;
    const tick = () => {
      fetchWorkoutSessionLiveSummary({
        workoutSessionId: liveNow.workout_session_id,
      })
        .then((live) => {
          if (live) setLiveNow(live);
          else setLiveNow(null);
        })
        .catch(() => {});
    };
    const id = window.setInterval(tick, 15000);
    return () => window.clearInterval(id);
  }, [liveNow?.workout_session_id]);

  const kpis = overview?.kpis || {};
  const oversight = overview?.oversight || {};

  const kpiCards = [
    {
      label: 'Total Volume',
      value: formatVolume(kpis.total_volume_lb),
      note: 'This range',
      Icon: IconChartBar,
    },
    {
      label: 'Workouts',
      value:
        kpis.workouts_scheduled != null
          ? `${kpis.workouts_completed ?? 0}/${kpis.workouts_scheduled}`
          : String(kpis.workouts_completed ?? 0),
      note:
        kpis.compliance_pct != null
          ? `${formatPct(kpis.compliance_pct)} compliance`
          : 'Completed',
      Icon: IconCheck,
    },
    {
      label: 'New PRs',
      value: kpis.new_prs ?? 0,
      note: 'Max updates',
      Icon: IconTrophy,
    },
    {
      label: 'Avg RPE',
      value: kpis.average_rpe != null ? Number(kpis.average_rpe).toFixed(1) : '—',
      note: 'Logged sets',
      Icon: IconActivity,
    },
    {
      label: 'Strength',
      value: formatSignedPct(kpis.strength_change_pct),
      note: 'Key lifts · this range',
      Icon: IconTrendingUp,
    },
  ];

  return (
    <div className={classes.page}>
      <header className={classes.topBar}>
        <div>
          <p className={classes.eyebrow}>Training</p>
          <h1 className={classes.title}>Progress</h1>
          <p className={classes.subtitle}>
            Train smart. Get stronger. Oversight you can act on.
          </p>
        </div>
        <DatePickerInput
          type="range"
          value={range || [null, null]}
          onChange={(next) => {
            if (!next?.[0] || !next?.[1]) return;
            setRange(next);
          }}
          valueFormat="MMM D, YYYY"
          allowSingleDateInRange
          className={classes.rangePicker}
          disabled={!rangeReady}
        />
      </header>

      {liveNow ? (
        <section className={classes.liveStrip}>
          <div className={classes.liveMain}>
            <span className={classes.liveBadge}>Live now</span>
            <h2 className={classes.liveTitle}>
              {liveNow.session_label || 'Workout in progress'}
            </h2>
            <div className={classes.liveMeta}>
              <span>{formatClock(liveNow.elapsed_seconds)}</span>
              <span>
                {liveNow.sets_completed ?? 0}/{liveNow.sets_total ?? 0} sets
              </span>
              <span>{formatVolume(liveNow.volume_lb)}</span>
              {liveNow.last_bpm != null ? (
                <span className={classes.liveHr}>
                  <IconHeartbeat size={16} stroke={1.75} />
                  {liveNow.last_bpm} bpm
                  {liveNow.avg_bpm_5min != null
                    ? ` · 5m avg ${liveNow.avg_bpm_5min}`
                    : ''}
                </span>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className={classes.liveCta}
            onClick={() => navigate('/workout')}
          >
            <IconPlayerPlay size={16} stroke={2} />
            Back to workout
          </button>
        </section>
      ) : null}

      <nav className={classes.tabs} aria-label="Progress sections">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={[
              classes.tab,
              tab === item.id ? classes.tabActive : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {loading ? (
        <section className={classes.card}>
          <p className={classes.empty}>Loading progress…</p>
        </section>
      ) : null}

      {!loading && tab === 'overview' && overview ? (
        <div className={classes.stack}>
          <section className={classes.card}>
            <p className={classes.sectionLabel}>At a glance</p>
            <div className={classes.kpiGrid}>
              {kpiCards.map((stat) => {
                const StatIcon = stat.Icon;
                return (
                  <div key={stat.label} className={classes.statCard}>
                    <div className={classes.statTop}>
                      <p className={classes.statLabel}>{stat.label}</p>
                      <span className={classes.statIcon}>
                        <StatIcon size={16} stroke={1.75} />
                      </span>
                    </div>
                    <p className={classes.statValue}>{stat.value}</p>
                    <p className={classes.statNote}>{stat.note}</p>
                  </div>
                );
              })}
            </div>
          </section>

          <section className={classes.card}>
            <p className={classes.sectionLabel}>Oversight</p>
            <div className={classes.oversightGrid}>
              <div className={classes.miniCard}>
                <p className={classes.miniLabel}>Program pulse</p>
                <p className={classes.miniValue}>
                  {formatPct(oversight.program_pulse?.completion_rate_pct)}
                </p>
                <p className={classes.miniMeta}>
                  {oversight.program_pulse?.workouts_completed_slots ?? 0}/
                  {oversight.program_pulse?.workouts_scheduled ?? 0} slots ·{' '}
                  {oversight.program_pulse?.sets_completed ?? 0} sets done ·{' '}
                  {oversight.program_pulse?.sets_skipped ?? 0} skipped
                </p>
              </div>
              <div className={classes.miniCard}>
                <p className={classes.miniLabel}>Acute : chronic</p>
                <p className={classes.miniValue}>
                  {oversight.acute_chronic_volume?.ratio ?? '—'}
                </p>
                <p className={classes.miniMeta}>
                  {oversight.acute_chronic_volume?.flag || 'unknown'} · 7d{' '}
                  {formatVolume(oversight.acute_chronic_volume?.acute_volume_lb)}{' '}
                  vs week avg{' '}
                  {formatVolume(
                    oversight.acute_chronic_volume?.chronic_week_avg_lb,
                  )}
                </p>
              </div>
              <div className={classes.miniCard}>
                <p className={classes.miniLabel}>Adherence</p>
                <p className={classes.miniValue}>
                  {oversight.prescription_adherence?.weight_ratio != null
                    ? `${Math.round(
                        oversight.prescription_adherence.weight_ratio * 100,
                      )}%`
                    : '—'}
                </p>
                <p className={classes.miniMeta}>
                  Weight vs prescribed · reps{' '}
                  {oversight.prescription_adherence?.reps_ratio != null
                    ? `${Math.round(
                        oversight.prescription_adherence.reps_ratio * 100,
                      )}%`
                    : '—'}
                </p>
              </div>
              <div className={classes.miniCard}>
                <p className={classes.miniLabel}>Near-max</p>
                <p className={classes.miniValue}>
                  {formatPct(oversight.near_max_exposure?.hard_set_pct, 1)}
                </p>
                <p className={classes.miniMeta}>
                  {oversight.near_max_exposure?.hard_sets ?? 0}/
                  {oversight.near_max_exposure?.total_sets ?? 0} hard sets
                </p>
              </div>
            </div>
          </section>

          <div className={classes.twoCol}>
            <section className={classes.card}>
              <p className={classes.sectionLabel}>Weekly training load</p>
              <WeekLoadChart weeks={overview.weekly_training_load} />
              <p className={classes.legend}>
                <span className={classes.legendVol} /> Volume
                <span className={classes.legendHard} /> Hard sets
              </p>
            </section>
            <section className={classes.card}>
              <p className={classes.sectionLabel}>Key lifts</p>
              {(overview.key_lifts || []).length === 0 ? (
                <p className={classes.empty}>No exercise maxes yet.</p>
              ) : (
                <ul className={classes.list}>
                  {overview.key_lifts.map((lift) => (
                    <li key={lift.exercise_id} className={classes.row}>
                      <div className={classes.rowMain}>
                        <span className={classes.rowName}>
                          {lift.exercise_name}
                        </span>
                        <span className={classes.rowMeta}>
                          {lift.bodyweight_ratio != null
                            ? `${lift.bodyweight_ratio}× BW`
                            : '—'}
                          {lift.period_delta != null
                            ? ` · ${lift.period_delta > 0 ? '+' : ''}${lift.period_delta} ${lift.unit || 'lb'}`
                            : ''}
                        </span>
                      </div>
                      <span className={classes.rowStatus}>
                        {lift.estimated_max != null
                          ? `${lift.estimated_max} ${lift.unit || 'lb'}`
                          : '—'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <div className={classes.twoCol}>
            <section className={classes.card}>
              <p className={classes.sectionLabel}>Strength progress</p>
              {(overview.strength_progress || []).length === 0 ? (
                <p className={classes.empty}>No max history yet.</p>
              ) : (
                <div className={classes.seriesStack}>
                  {overview.strength_progress.map((series) => (
                    <div key={series.exercise_id} className={classes.seriesCard}>
                      <div className={classes.seriesHead}>
                        <span>{series.exercise_name}</span>
                        <span>
                          {series.points?.length
                            ? series.points[series.points.length - 1]
                                .estimated_max
                            : '—'}
                        </span>
                      </div>
                      <Sparkline points={series.points} />
                    </div>
                  ))}
                </div>
              )}
            </section>
            <section className={classes.card}>
              <p className={classes.sectionLabel}>Movement mix</p>
              <BarList
                buckets={(overview.movement_distribution?.buckets || []).map(
                  (row) => ({
                    ...row,
                    name: row.name,
                  }),
                )}
              />
            </section>
          </div>

          <div className={classes.twoCol}>
            <section className={classes.card}>
              <p className={classes.sectionLabel}>Intensity</p>
              <BarList
                buckets={(overview.intensity_distribution?.buckets || []).map(
                  (row) => ({
                    ...row,
                    name: INTENSITY_LABELS[row.key] || row.key,
                  }),
                )}
              />
            </section>
            <section className={classes.card}>
              <p className={classes.sectionLabel}>Rep ranges</p>
              <BarList
                buckets={(overview.rep_range_distribution?.buckets || [])
                  .filter((row) => Number(row.count) > 0)
                  .map((row) => ({
                    ...row,
                    name: REP_LABELS[row.key] || row.key,
                  }))}
              />
            </section>
          </div>

          <div className={classes.twoCol}>
            <section className={classes.card}>
              <p className={classes.sectionLabel}>Recent PRs</p>
              {(overview.recent_prs || []).length === 0 ? (
                <p className={classes.empty}>No PRs in this range.</p>
              ) : (
                <ul className={classes.list}>
                  {overview.recent_prs.map((pr) => (
                    <li
                      key={`${pr.exercise_id}-${pr.recorded_at}`}
                      className={classes.row}
                    >
                      <span className={classes.rowDate}>
                        {formatShortDate(pr.recorded_at)}
                      </span>
                      <div className={classes.rowMain}>
                        <span className={classes.rowName}>
                          {pr.exercise_name}
                        </span>
                      </div>
                      <span className={classes.rowStatus}>
                        {pr.estimated_max} {pr.unit || 'lb'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section className={classes.card}>
              <p className={classes.sectionLabel}>Heart</p>
              <div className={classes.heartGrid}>
                <div className={classes.miniCard}>
                  <p className={classes.miniLabel}>
                    <IconHeartbeat size={14} stroke={1.75} /> Resting HR
                  </p>
                  <p className={classes.miniValue}>
                    {overview.heart?.resting_hr?.latest_value != null
                      ? `${overview.heart.resting_hr.latest_value} ${overview.heart.resting_hr.unit || 'bpm'}`
                      : '—'}
                  </p>
                  <p className={classes.miniMeta}>
                    {overview.heart?.hr_linked_sessions ?? 0} HR-linked sessions
                  </p>
                </div>
                <BarList
                  buckets={(overview.heart?.zone_mix || []).map((row) => ({
                    ...row,
                    name: ZONE_LABELS[row.key] || row.key,
                  }))}
                />
              </div>
            </section>
          </div>

          <div className={classes.twoCol}>
            <section className={classes.card}>
              <p className={classes.sectionLabel}>
                <IconScale size={14} stroke={1.75} /> Bodyweight
              </p>
              <p className={classes.miniValue}>
                {overview.bodyweight_trend?.latest_value != null
                  ? `${overview.bodyweight_trend.latest_value} ${overview.bodyweight_trend.unit || 'lb'}`
                  : '—'}
              </p>
              <Sparkline
                points={overview.bodyweight_trend?.points || []}
                valueKey="value"
              />
            </section>
            <section className={classes.card}>
              <p className={classes.sectionLabel}>
                <IconClock size={14} stroke={1.75} /> Session duration
              </p>
              <p className={classes.miniValue}>
                {formatDuration(
                  overview.workout_duration?.average_duration_seconds,
                )}{' '}
                avg
              </p>
              <WeekLoadChart
                weeks={(overview.workout_duration?.weeks || []).map((week) => ({
                  week_start: week.week_start,
                  volume_lb: week.avg_duration_seconds,
                  hard_sets: week.session_count,
                }))}
              />
            </section>
          </div>

          <section className={classes.card} id="trophy-case">
            <p className={classes.sectionLabel}>Trophy Case</p>
            <TrophyCaseGrid
              items={achievements}
              emptyMessage="No achievements available yet."
            />
          </section>
        </div>
      ) : null}

      {!loading && tab === 'strength' ? (
        <div className={classes.stack}>
          {tabLoading || !strength ? (
            <section className={classes.card}>
              <p className={classes.empty}>Loading strength…</p>
            </section>
          ) : (
            <>
              <section className={classes.card}>
                <p className={classes.sectionLabel}>Key lifts</p>
                {(strength.key_lifts || []).length === 0 ? (
                  <p className={classes.empty}>No exercise maxes yet.</p>
                ) : (
                  <ul className={classes.list}>
                    {strength.key_lifts.map((lift) => (
                      <li key={lift.exercise_id} className={classes.row}>
                        <div className={classes.rowMain}>
                          <span className={classes.rowName}>
                            <IconBarbell size={14} stroke={1.75} />{' '}
                            {lift.exercise_name}
                          </span>
                          <span className={classes.rowMeta}>
                            {lift.bodyweight_ratio != null
                              ? `${lift.bodyweight_ratio}× BW`
                              : '—'}
                          </span>
                        </div>
                        <span className={classes.rowStatus}>
                          {lift.estimated_max != null
                            ? `${lift.estimated_max} ${lift.unit || 'lb'}`
                            : '—'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              <section className={classes.card}>
                <p className={classes.sectionLabel}>Max history</p>
                <div className={classes.seriesStack}>
                  {(strength.strength_progress || []).map((series) => (
                    <div key={series.exercise_id} className={classes.seriesCard}>
                      <div className={classes.seriesHead}>
                        <span>{series.exercise_name}</span>
                      </div>
                      <Sparkline points={series.points} />
                    </div>
                  ))}
                </div>
              </section>
              <section className={classes.card}>
                <p className={classes.sectionLabel}>Near-max by lift</p>
                <BarList
                  buckets={(strength.near_max_by_lift || []).map((row) => ({
                    name: row.exercise_name,
                    count: row.hard_sets,
                    pct: row.hard_set_pct,
                  }))}
                />
              </section>
              <section className={classes.card}>
                <p className={classes.sectionLabel}>Recent PRs</p>
                {(strength.recent_prs || []).length === 0 ? (
                  <p className={classes.empty}>No PRs in this range.</p>
                ) : (
                  <ul className={classes.list}>
                    {strength.recent_prs.map((pr) => (
                      <li
                        key={`${pr.exercise_id}-${pr.recorded_at}`}
                        className={classes.row}
                      >
                        <span className={classes.rowDate}>
                          {formatShortDate(pr.recorded_at)}
                        </span>
                        <div className={classes.rowMain}>
                          <span className={classes.rowName}>
                            {pr.exercise_name}
                          </span>
                        </div>
                        <span className={classes.rowStatus}>
                          {pr.estimated_max} {pr.unit || 'lb'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>
      ) : null}

      {!loading && tab === 'volume' ? (
        <div className={classes.stack}>
          {tabLoading || !volume ? (
            <section className={classes.card}>
              <p className={classes.empty}>Loading volume…</p>
            </section>
          ) : (
            <>
              <section className={classes.card}>
                <p className={classes.sectionLabel}>Density</p>
                <p className={classes.miniValue}>
                  {volume.volume_per_minute?.volume_per_minute != null
                    ? `${volume.volume_per_minute.volume_per_minute} lb/min`
                    : '—'}
                </p>
                <p className={classes.miniMeta}>
                  {formatVolume(volume.volume_per_minute?.volume_lb)} over{' '}
                  {volume.volume_per_minute?.minutes ?? 0} min
                </p>
              </section>
              <section className={classes.card}>
                <p className={classes.sectionLabel}>Weekly volume</p>
                <WeekLoadChart weeks={volume.weekly_volume} />
              </section>
              <div className={classes.twoCol}>
                <section className={classes.card}>
                  <p className={classes.sectionLabel}>Push : pull</p>
                  <p className={classes.miniValue}>
                    {volume.push_pull?.ratio ?? '—'}
                  </p>
                  <p className={classes.miniMeta}>
                    {volume.push_pull?.left_count ?? 0} push ·{' '}
                    {volume.push_pull?.right_count ?? 0} pull
                  </p>
                </section>
                <section className={classes.card}>
                  <p className={classes.sectionLabel}>Squat : hinge</p>
                  <p className={classes.miniValue}>
                    {volume.squat_hinge?.ratio ?? '—'}
                  </p>
                  <p className={classes.miniMeta}>
                    {volume.squat_hinge?.left_count ?? 0} squat ·{' '}
                    {volume.squat_hinge?.right_count ?? 0} hinge
                  </p>
                </section>
              </div>
              <section className={classes.card}>
                <p className={classes.sectionLabel}>Top exercises</p>
                <BarList
                  buckets={(volume.top_exercises || []).map((row) => ({
                    name: row.exercise_name,
                    count: row.volume_lb,
                    pct: null,
                  }))}
                  valueKey="count"
                />
              </section>
              <div className={classes.twoCol}>
                <section className={classes.card}>
                  <p className={classes.sectionLabel}>Movement</p>
                  <BarList buckets={volume.movement_distribution?.buckets} />
                </section>
                <section className={classes.card}>
                  <p className={classes.sectionLabel}>Force vector</p>
                  <BarList
                    buckets={volume.force_vector_distribution?.buckets}
                  />
                </section>
              </div>
              <section className={classes.card}>
                <p className={classes.sectionLabel}>Training quality</p>
                <BarList
                  buckets={volume.training_quality_distribution?.buckets}
                />
              </section>
            </>
          )}
        </div>
      ) : null}

      {!loading && tab === 'compliance' ? (
        <div className={classes.stack}>
          {tabLoading || !compliance ? (
            <section className={classes.card}>
              <p className={classes.empty}>Loading compliance…</p>
            </section>
          ) : (
            <>
              <section className={classes.card}>
                <p className={classes.sectionLabel}>Program pulse</p>
                <p className={classes.miniValue}>
                  {formatPct(compliance.program_pulse?.completion_rate_pct)}
                </p>
                <p className={classes.miniMeta}>
                  Scheduled {compliance.program_pulse?.workouts_scheduled ?? 0} ·
                  completed slots{' '}
                  {compliance.program_pulse?.workouts_completed_slots ?? 0} ·
                  missed {compliance.program_pulse?.workouts_missed ?? 0} ·
                  overlays {compliance.program_pulse?.overlay_completed ?? 0}
                </p>
              </section>
              <div className={classes.oversightGrid}>
                <div className={classes.miniCard}>
                  <p className={classes.miniLabel}>Slot status</p>
                  <p className={classes.miniMeta}>
                    Pending {compliance.slot_status_breakdown?.pending ?? 0}
                    <br />
                    Completed {compliance.slot_status_breakdown?.completed ?? 0}
                    <br />
                    Missed {compliance.slot_status_breakdown?.missed ?? 0}
                    <br />
                    Skipped {compliance.slot_status_breakdown?.skipped ?? 0}
                  </p>
                </div>
                <div className={classes.miniCard}>
                  <p className={classes.miniLabel}>Set skip rate</p>
                  <p className={classes.miniValue}>
                    {formatPct(compliance.set_skip_rate?.skip_pct, 1)}
                  </p>
                  <p className={classes.miniMeta}>
                    {compliance.set_skip_rate?.skipped ?? 0} skipped /{' '}
                    {(compliance.set_skip_rate?.completed || 0) +
                      (compliance.set_skip_rate?.skipped || 0)}{' '}
                    finished
                  </p>
                </div>
                <div className={classes.miniCard}>
                  <p className={classes.miniLabel}>On time vs late</p>
                  <p className={classes.miniValue}>
                    {compliance.on_time_vs_late?.on_time ?? 0}:
                    {compliance.on_time_vs_late?.late ?? 0}
                  </p>
                  <p className={classes.miniMeta}>
                    {compliance.on_time_vs_late?.total ?? 0} completed slots
                  </p>
                </div>
                <div className={classes.miniCard}>
                  <p className={classes.miniLabel}>Adherence</p>
                  <p className={classes.miniMeta}>
                    Weight{' '}
                    {compliance.adherence?.weight_ratio != null
                      ? `${Math.round(compliance.adherence.weight_ratio * 100)}%`
                      : '—'}
                    <br />
                    Reps{' '}
                    {compliance.adherence?.reps_ratio != null
                      ? `${Math.round(compliance.adherence.reps_ratio * 100)}%`
                      : '—'}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
